import type { RestSchema } from "@mountaya/client";
import { z } from "zod";

import type { RestClientConfig } from "../../rest/rules/sdk";
import { organizationsFor } from "./sdk";

/**
 * UsageTileResourceType is one Tile API resource kind, and it is the CONTRACT's type
 * rather than a local one: the vocabulary belongs to the Tile API, so a resource kind it
 * gains reads here the moment the client carries it.
 *
 * Every `satisfies z.ZodType<…>` in this module is HALF the guarantee, for the reason
 * `roles.ts` spells out: a schema producing a NARROWER union still satisfies it, because a
 * narrower output is assignable to a wider one. It catches a member a schema invents that
 * the contract lacks; it does not catch one the contract gained and the schema lacks,
 * which is the direction that breaks, since the enum then rejects a real usage point at
 * the parse. `usage.test.ts` holds that direction with a type-level equality per enum.
 */
export type UsageTileResourceType = RestSchema.UsageTileResourceType;

/**
 * usageTileResourceTypeSchema mirrors the backend UsageTileResourceType enum: the
 * kinds of resource the Tile API serves. The Usage page filters tile usage by one
 * of these and labels each series.
 *
 * `unknown` is not a resource: it is what a request refused by the publishable-key
 * gate before any route matched is recorded under. It is deliberately absent from
 * the resource-type FILTER select, a gate refusal is never a success, so it can
 * never appear in the billed-usage chart, and reads only in the reliability
 * breakdown.
 *
 * `terrain` is the base map served through the Tile API (its style, the tiles of the
 * sources it draws, its sprites and glyphs, the elevation raster). It is recorded so the
 * organization sees what its maps draw and is never counted, whatever surface asked for it:
 * see {@link UNCOUNTED_TILE_RESOURCE_TYPES}.
 */
export const usageTileResourceTypeSchema = z.enum([
  "itinerary",
  "itineraries",
  "zones",
  "overlay",
  "collection",
  "terrain",
  "unknown",
]) satisfies z.ZodType<UsageTileResourceType>;

/**
 * UNCOUNTED_TILE_RESOURCE_TYPES are the Tile API resource kinds recorded and never counted
 * toward the per-minute limit or the monthly allowance, on any surface. A counted tile figure
 * leaves them out, so the base map a key draws through the Tile API never reads as counted
 * requests.
 */
export const UNCOUNTED_TILE_RESOURCE_TYPES = [
  "terrain",
] as const satisfies readonly UsageTileResourceType[];

/**
 * isCountedTileResourceType reports whether tile usage of a resource kind counts toward the
 * Tile API's per-minute limit and monthly allowance on a counted surface: every kind does but
 * `terrain`.
 */
export const isCountedTileResourceType = (resourceType: UsageTileResourceType): boolean =>
  !UNCOUNTED_TILE_RESOURCE_TYPES.some((uncounted) => uncounted === resourceType);

/**
 * UsageTileSurface is one tile-request surface, and it is the contract's type.
 */
export type UsageTileSurface = RestSchema.UsageTileSurface;

/**
 * usageTileSurfaceSchema mirrors the contract's UsageTileSurface enum: which surface a tile
 * request arrived from. "api" is the organization's own client on one of its keys and
 * "embedding" the Mountaya-hosted embed, drawn on one of them: the two surfaces that count toward
 * the Tile API's per-minute limit and its monthly allowance, "embedding" recorded apart so the
 * organization sees what its embeds drew. No tile is metered onto an invoice: the Tile API is
 * held by agreement (ADR 0097). The others are Mountaya's own surfaces,
 * recorded so the organization sees them and never counted: "studio-online" the Map Studio in
 * Mountaya's web application, "studio-embedded" the Embedded Studio on the organization's own
 * site and "share" its share links.
 */
export const usageTileSurfaceSchema = z.enum([
  "api",
  "studio-online",
  "studio-embedded",
  "embedding",
  "share",
]) satisfies z.ZodType<UsageTileSurface>;

/**
 * BILLED_TILE_SURFACES are the tile usage surfaces that count toward the Tile API's per-minute
 * limit and its monthly allowance, in the order the usage page lists them: the organization's own
 * client, and the hosted embed drawn on its keys. Counted, not metered: no tile reaches an
 * invoice (ADR 0097).
 */
export const BILLED_TILE_SURFACES = [
  "api",
  "embedding",
] as const satisfies readonly UsageTileSurface[];

/**
 * UsageDataSurface is where a day's Data API usage was made, and it is the contract's type.
 */
export type UsageDataSurface = RestSchema.UsageDataSurface;

/**
 * usageDataSurfaceSchema mirrors the contract's UsageDataSurface enum: "api" is a Data API
 * request the organization's own client made on one of its keys, "mcp" one an MCP connector made
 * for it, "studio-online" one the Map Studio in Mountaya's web application made while a member
 * had the organization open, and "studio-embedded" one the Embedded Studio made on the
 * organization's own site, its routing for the writes it saves included. Only "api" and "mcp"
 * count toward the Data API's per-minute limit, its monthly allowance and its invoice: the two
 * studios are recorded so the organization sees them, and are never counted.
 */
export const usageDataSurfaceSchema = z.enum([
  "api",
  "mcp",
  "studio-online",
  "studio-embedded",
]) satisfies z.ZodType<UsageDataSurface>;

/**
 * BILLED_DATA_SURFACES are the data usage surfaces that count toward the Data API's allowance and
 * its invoice, in the order the usage page lists them.
 */
export const BILLED_DATA_SURFACES = ["api", "mcp"] as const satisfies readonly UsageDataSurface[];

/**
 * isBilledDataSurface reports whether a data usage surface counts toward the Data API's allowance
 * and invoice: "api" and "mcp" do, the two studios never do.
 */
export const isBilledDataSurface = (surface: UsageDataSurface): boolean =>
  BILLED_DATA_SURFACES.some((billed) => billed === surface);

/**
 * UsageStatusClass is one request status class, and it is the contract's type.
 */
export type UsageStatusClass = RestSchema.UsageStatusClass;

/**
 * usageStatusClassSchema mirrors the backend UsageStatusClass enum: the HTTP status class a
 * request was accounted under, and the dimension availability is computed from. The backend
 * derives it from the request's terminal error rather than from the response status, so a
 * Data API query that fails inside an HTTP 200 still reads "5xx" here and one formula spans
 * both APIs.
 *
 * Availability is 2xx / (2xx + 5xx). "4xx" is excluded on purpose: a malformed request from
 * the caller is not a breach of the availability commitment.
 */
export const usageStatusClassSchema = z.enum([
  "2xx",
  "4xx",
  "5xx",
]) satisfies z.ZodType<UsageStatusClass>;

/**
 * UsageDataResourceType is one Data API resource kind, and it is the contract's type.
 */
export type UsageDataResourceType = RestSchema.UsageDataResourceType;

/**
 * usageDataResourceTypeSchema mirrors the backend UsageDataResourceType enum: the
 * Data API operations usage is aggregated by.
 *
 * `unknown` is not an operation: it is what a request refused by the
 * publishable-key gate before any resolver matched is recorded under. It is
 * deliberately absent from the resource-type FILTER select, for the same reason as
 * its tile twin.
 */
export const usageDataResourceTypeSchema = z.enum([
  "search",
  "geocode",
  "reverse-geocode",
  "pois",
  "directions",
  "suggestions",
  "matrix",
  "isochrones",
  "analyze-geometry",
  "terrain-samples",
  "terrain-profile",
  "terrain-grid",
  "itinerary",
  "export-itinerary",
  "unknown",
]) satisfies z.ZodType<UsageDataResourceType>;

/**
 * UsageMetric is one Data API usage measure, and it is the contract's type.
 */
export type UsageMetric = RestSchema.UsageMetric;

/**
 * usageMetricSchema mirrors the backend UsageMetric enum: the measure a Data API
 * usage point aggregates (request count, results returned, etc.).
 */
export const usageMetricSchema = z.enum([
  "requests",
  "results-returned",
  "targets-executed",
  "routes",
  "waypoints",
  "coordinates",
  "elements",
  "polygons",
  "samples",
]) satisfies z.ZodType<UsageMetric>;

/**
 * DATA_METRICS_BY_RESOURCE_TYPE is the source of truth for which metrics each Data
 * API resource type can emit. It mirrors the backend `dataMetricsByResourceType`
 * (backend/internal/domain/usage/metrics.go) and must stay in sync with it: the
 * backend rejects an incompatible (resource_type, metric) pair with a 400, so the
 * usage filter scopes its metric select to the chosen resource type to keep that
 * pairing unreachable from the UI. Every resource type emits the universal
 * `requests` metric.
 *
 * `unknown` emits ONLY `requests`: the request never reached a resolver, so no
 * detail metric could have a value.
 *
 * `coordinates` belongs to `analyze-geometry` alone: no stored row pairs it with
 * `directions` any more, so the backend answers that pair with the 400 of any pair
 * that does not exist.
 */
export const DATA_METRICS_BY_RESOURCE_TYPE: Record<UsageDataResourceType, readonly UsageMetric[]> =
  {
    "analyze-geometry": ["requests", "coordinates"],
    directions: ["requests", "routes", "waypoints"],
    "export-itinerary": ["requests"],
    geocode: ["requests", "results-returned"],
    isochrones: ["requests", "polygons"],
    itinerary: ["requests"],
    matrix: ["requests", "elements"],
    pois: ["requests", "results-returned"],
    "reverse-geocode": ["requests", "results-returned"],
    search: ["requests", "targets-executed", "results-returned"],
    suggestions: ["requests", "routes"],
    "terrain-grid": ["requests", "samples"],
    "terrain-profile": ["requests", "samples"],
    "terrain-samples": ["requests", "samples"],
    unknown: ["requests"],
  };

/**
 * dataMetricsForResourceType returns the metrics offerable for a Data API resource
 * type. With no resource type selected it returns every metric, since a metric-only
 * query legitimately spans all resource types. The usage filter uses it to scope its
 * metric select to the chosen resource type.
 */
export const dataMetricsForResourceType = (
  resourceType: UsageDataResourceType | undefined,
): readonly UsageMetric[] =>
  resourceType ? DATA_METRICS_BY_RESOURCE_TYPE[resourceType] : usageMetricSchema.options;

/**
 * UsageTilesPoint is one daily Tile API usage point, and it is the contract's type
 * (`UsageTilePoint`). The schema below parses every field the contract declares, so the alias
 * claims nothing the parse drops.
 */
export type UsageTilesPoint = RestSchema.UsageTilePoint;

/**
 * usageTilesPointSchema is one daily Tile API usage aggregate, for ONE surface. `date`
 * stays a plain string: the backend serializes it as `format: date` (a calendar day,
 * `YYYY-MM-DD`, no time component) which the date reviver does not touch, only full ISO
 * date-time strings are revived to Date.
 *
 * A day on which several surfaces were used yields one point per surface for the same resource
 * type, rather than one combined total: a caller that needs the combined figure adds them, and a
 * caller that needs the counted figure adds api and embedding, the two surfaces that count; no
 * tile is metered (ADR 0097). `status_class` splits each of those the same way, so the counted
 * figure and the availability figure come off one read.
 */
export const usageTilesPointSchema = z.object({
  date: z.string(),
  requests: z.number(),
  resource_type: usageTileResourceTypeSchema,
  status_class: usageStatusClassSchema,
  surface: usageTileSurfaceSchema,
}) satisfies z.ZodType<UsageTilesPoint>;

/**
 * UsageDataPoint is one daily Data API usage point, and it is the contract's type. The
 * schema below parses every field the contract declares, so the alias claims nothing the
 * parse drops.
 */
export type UsageDataPoint = RestSchema.UsageDataPoint;

/**
 * usageDataPointSchema is one daily Data API usage aggregate, for ONE surface. `date` stays a
 * plain string for the same reason as the tile point (`format: date`, not revived).
 * `status_class` splits each aggregate by how the request ended, so one read answers both what
 * was billed and what was available, and `surface` splits it by where it was made, so the
 * billed figure ("api" and "mcp") and the two studios' usage (never counted) come off one read
 * too.
 */
export const usageDataPointSchema = z.object({
  date: z.string(),
  metric: usageMetricSchema,
  resource_type: usageDataResourceTypeSchema,
  status_class: usageStatusClassSchema,
  surface: usageDataSurfaceSchema,
  value: z.number(),
}) satisfies z.ZodType<UsageDataPoint>;

/**
 * usageTilesFiltersSchema is the Tile API usage query filter. The field names are
 * the wire query-param names, so a filter maps onto the request one-to-one.
 * `used_publishable_key` is named exactly so — a bare `publishable_key` query
 * param would be read by the auth middleware as a credential, so the usage filter
 * carries the `used_` prefix instead.
 */
export const usageTilesFiltersSchema = z.object({
  end_at: z.string().optional(),
  resource_id: z.string().optional(),
  resource_type: usageTileResourceTypeSchema.optional(),
  start_at: z.string().optional(),
  used_publishable_key: z.string().optional(),
});

/**
 * UsageTilesFilters is the Tile API usage filter, derived from
 * usageTilesFiltersSchema.
 */
export type UsageTilesFilters = z.infer<typeof usageTilesFiltersSchema>;

/**
 * usageDataFiltersSchema is the Data API usage query filter. It mirrors the tile
 * filter but swaps `resource_id` for `metric`: Data API usage is sliced by metric
 * rather than a single resource id. `surface` narrows it to where the usage was made.
 */
export const usageDataFiltersSchema = z.object({
  end_at: z.string().optional(),
  metric: usageMetricSchema.optional(),
  resource_type: usageDataResourceTypeSchema.optional(),
  start_at: z.string().optional(),
  surface: usageDataSurfaceSchema.optional(),
  used_publishable_key: z.string().optional(),
});

/**
 * UsageDataFilters is the Data API usage filter, derived from
 * usageDataFiltersSchema.
 */
export type UsageDataFilters = z.infer<typeof usageDataFiltersSchema>;

/**
 * usageTilesResponseSchema is the REST envelope around the tile usage series ([]
 * when no usage in range).
 */
const usageTilesResponseSchema = z.object({ data: z.array(usageTilesPointSchema) });

/**
 * usageDataResponseSchema is the REST envelope around the data usage series ([]
 * when no usage in range).
 */
const usageDataResponseSchema = z.object({ data: z.array(usageDataPointSchema) });

/**
 * usageFilterValue keeps a filter value only where it narrows something.
 *
 * An unset filter and a cleared one mean the same thing to the page — read everything —
 * but they do not mean the same thing on the wire: an empty `resource_type=` is a value
 * the backend has to reject, where an omitted parameter is the unfiltered read the page
 * asked for. Folding the empty string back to undefined is what lets the transport drop
 * the parameter instead of sending it empty.
 */
const usageFilterValue = <T extends string>(value: T | undefined): T | undefined =>
  value === undefined || value.length === 0 ? undefined : value;

/**
 * usageTilesQueryKey is the TanStack Query key for an organization's tile usage.
 * The filters object is embedded so a filter change is a distinct cache entry,
 * letting keepPreviousData hold the prior series while the new one loads.
 */
export const usageTilesQueryKey = (organizationId: string, filters: UsageTilesFilters) =>
  ["organizations", organizationId, "usage", "tiles", filters] as const;

/**
 * usageDataQueryKey is the TanStack Query key for an organization's data usage,
 * filters embedded as with tiles.
 */
export const usageDataQueryKey = (organizationId: string, filters: UsageDataFilters) =>
  ["organizations", organizationId, "usage", "data", filters] as const;

/**
 * fetchOrganizationUsageTiles is a hookless query-fn reading an organization's
 * Tile API usage series for the given filter. The filter's fields ARE the wire
 * query params, so each is named through to the request and the response is
 * Zod-parsed at the boundary. A non-ok response (the 400 an incompatible filter
 * pairing produces) throws a typed error so the page surfaces the message.
 */
export const fetchOrganizationUsageTiles = async (
  config: RestClientConfig,
  organizationId: string,
  filters: UsageTilesFilters,
): Promise<UsageTilesPoint[]> => {
  const envelope = await organizationsFor(config).getOrganizationUsageTiles({
    organization_id: organizationId,
    query: {
      end_at: usageFilterValue(filters.end_at),
      resource_id: usageFilterValue(filters.resource_id),
      resource_type: usageFilterValue(filters.resource_type),
      start_at: usageFilterValue(filters.start_at),
      used_publishable_key: usageFilterValue(filters.used_publishable_key),
    },
  });

  return usageTilesResponseSchema.parse(envelope).data;
};

/**
 * fetchOrganizationUsageData is a hookless query-fn reading an organization's
 * Data API usage series for the given filter. Same name-through/parse/throw
 * contract as the tile usage fetch.
 */
export const fetchOrganizationUsageData = async (
  config: RestClientConfig,
  organizationId: string,
  filters: UsageDataFilters,
): Promise<UsageDataPoint[]> => {
  const envelope = await organizationsFor(config).getOrganizationUsageData({
    organization_id: organizationId,
    query: {
      end_at: usageFilterValue(filters.end_at),
      metric: usageFilterValue(filters.metric),
      resource_type: usageFilterValue(filters.resource_type),
      start_at: usageFilterValue(filters.start_at),
      surface: usageFilterValue(filters.surface),
      used_publishable_key: usageFilterValue(filters.used_publishable_key),
    },
  });

  return usageDataResponseSchema.parse(envelope).data;
};

/**
 * organizationUsageTilesQueryOptions bundles the tile-usage query key + fetch fn.
 */
export const organizationUsageTilesQueryOptions = (
  config: RestClientConfig,
  organizationId: string,
  filters: UsageTilesFilters,
) => ({
  queryFn: () => fetchOrganizationUsageTiles(config, organizationId, filters),
  queryKey: usageTilesQueryKey(organizationId, filters),
});

/**
 * organizationUsageDataQueryOptions bundles the data-usage query key + fetch fn.
 */
export const organizationUsageDataQueryOptions = (
  config: RestClientConfig,
  organizationId: string,
  filters: UsageDataFilters,
) => ({
  queryFn: () => fetchOrganizationUsageData(config, organizationId, filters),
  queryKey: usageDataQueryKey(organizationId, filters),
});
